import React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { InsurancePlan } from 'src/services/interfaces/insurance';
import { useApplicationStore } from 'src/store/application';

interface PHQPlanListStepProps {
  form: UseFormReturn<Record<string, unknown>>;
  formData: Partial<OnboardingSchema>;
  updateFormData: (data: Partial<OnboardingSchema>) => void;
  plans: InsurancePlan[];
  calculateCoverageLevel: (tags: string[]) => number;
  getPlanPrice: (plan: InsurancePlan, coverageLevel: number, selectedTier?: string, coverageAmount?: number) => number;
}

export const PHQPlanListStep: React.FC<PHQPlanListStepProps> = ({ 
  form,
  plans,
  calculateCoverageLevel,
  getPlanPrice
}) => {
  const { tier } = useApplicationStore();
  // Фильтруем только Major Medical планы
  const medicalPlans = plans.filter(plan => plan.category === 'Major Medical');

  const coverageLevels = [
    { value: 'Primary', label: 'Primary' },
    { value: 'Primary+Spouse', label: 'Primary + Spouse' },
    { value: 'Primary+Dependents', label: 'Primary + Dependents' },
    { value: 'Family', label: 'Family' },
  ];

  // Currently selected plan id to determine available tiers (subscribe to changes)
  const selectedPlanId = useWatch({ control: form.control, name: 'phqPlanId' });

  // Build list of available tiers from plan prices (fallback to first plan if none selected)
  const availableTiers = React.useMemo(() => {
    let planWithPrices = medicalPlans.find((p) => String(p.id) === String(selectedPlanId));
    if (!planWithPrices && medicalPlans.length > 0) {
      planWithPrices = medicalPlans[0];
    }
    if (planWithPrices && Array.isArray(planWithPrices.prices)) {
      return [...new Set(planWithPrices.prices.map((p) => p.tier).filter(Boolean))];
    }
    return [] as string[];
  }, [selectedPlanId, medicalPlans]);

  // Ensure form tier value is always valid
  React.useEffect(() => {
    if (availableTiers.length > 0) {
      const currentTier = form.getValues('tier') as unknown as string | undefined;
      if (!currentTier || !availableTiers.includes(currentTier)) {
        form.setValue('tier', availableTiers[0]);
      }
    }
  }, [availableTiers]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          PHQ Plan List
        </h2>
        <p className="text-gray-600 mb-6">
          Select your medical plan and coverage level.
        </p>
      </div>

      {/* Plan Selection */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Plan
          </label>
          <select
            {...form.register('phqPlanId')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a plan...</option>
            {medicalPlans.map((plan) => (
              <option key={plan.uid} value={plan.id}>
                {plan.name || plan.marketingName || 'Untitled Plan'}
              </option>
            ))}
          </select>
        </div>

        {/* Coverage Level Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Coverage Level
          </label>
          <select
            {...form.register('phqCoverageLevel')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select coverage level...</option>
            {coverageLevels.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>

        {/* Price ID Display (Read-only) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Price ID (Auto-calculated)
          </label>
          <input
            type="text"
            value="Will be calculated automatically"
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 text-gray-500 cursor-not-allowed"
          />
          <p className="text-sm text-gray-500 mt-1">
            Price ID is automatically determined based on plan, coverage level, and {tier}
          </p>
        </div>

        {/* Tier Selection */}
        {/* <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tier
          </label>
          <select
            {...form.register('tier')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select tier...</option>
            {availableTiers.map((tier: string) => (
              <option key={tier} value={tier}>
                {tier}
              </option>
            ))}
          </select>
        </div> */}
      </div>
    </div>
  );
}; 